import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface DropdownItem {
  key: string
  label: string
  icon?: ReactNode
  /** Ação destrutiva — texto vermelho + hover vermelho claro. */
  danger?: boolean
  onClick: () => void
  disabled?: boolean
  /** Renderiza separador horizontal antes deste item. */
  divider?: boolean
}

interface Props {
  items: DropdownItem[]
  /** Conteúdo do gatilho. Default: ícone três pontos vertical. */
  trigger?: ReactNode
  /** Alinhamento horizontal do painel relativo ao gatilho. */
  align?: 'left' | 'right'
  /** Tamanho do botão gatilho quando default. */
  size?: 'sm' | 'md'
  buttonClassName?: string
  menuClassName?: string
  ariaLabel?: string
}

/**
 * Menu dropdown padrão para ações de linha em DataTable e afins.
 *
 * IMPORTANTE: o painel é renderizado via React Portal no `document.body` com
 * `position: fixed`, calculado a partir do `getBoundingClientRect()` do botão.
 * Isso garante que o menu SEMPRE sobreponha qualquer ancestral com `overflow:
 * hidden | auto` (ex.: tabela com scroll horizontal) — sem criar barra de
 * rolagem extra ou ficar oculto.
 *
 * - Auto-flip vertical: se não couber abaixo do gatilho, abre acima.
 * - Reposiciona em scroll/resize.
 * - Fecha ao clicar fora, ESC ou ao rolar a página (UX padrão de dropdowns).
 */
export function DropdownMenu({
  items, trigger, align = 'right', size = 'sm',
  buttonClassName, menuClassName, ariaLabel = 'Mais ações',
}: Props) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; minWidth: number; flipUp: boolean }>({
    top: 0, left: 0, minWidth: 180, flipUp: false,
  })

  // Calcula posição absoluta na viewport sempre que abre ou em scroll/resize.
  const recalc = () => {
    const btn = btnRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    const panelHeightGuess = panelRef.current?.offsetHeight ?? Math.min(items.length * 36 + 8, 320)
    const panelWidthGuess  = panelRef.current?.offsetWidth  ?? 180

    const spaceBelow = window.innerHeight - rect.bottom
    const flipUp = spaceBelow < panelHeightGuess + 12 && rect.top > panelHeightGuess + 12

    const top  = flipUp ? rect.top - panelHeightGuess - 4 : rect.bottom + 4
    const left = align === 'right'
      ? Math.max(8, rect.right - panelWidthGuess)
      : Math.min(window.innerWidth - panelWidthGuess - 8, rect.left)

    setPos({ top, left, minWidth: Math.max(rect.width, panelWidthGuess), flipUp })
  }

  // Recalcula assim que abre (após mount do painel) e em scroll/resize.
  useLayoutEffect(() => {
    if (!open) return
    recalc()
    // Segundo pass quando painel já tem altura real medida
    const t = requestAnimationFrame(recalc)
    return () => cancelAnimationFrame(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, items.length, align])

  useEffect(() => {
    if (!open) return
    const onScroll = () => setOpen(false)
    const onResize = () => recalc()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (btnRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const btnSize = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9'

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}
        className={cn(
          'cursor-pointer inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition',
          btnSize,
          open && 'bg-gray-100 text-gray-700',
          buttonClassName,
        )}
      >
        {trigger ?? <MoreVertical size={16} />}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top:  pos.top,
            left: pos.left,
            minWidth: pos.minWidth,
            zIndex: 9999,
          }}
          className={cn(
            'bg-white ring-1 ring-gray-200 rounded-md py-1',
            menuClassName,
          )}
        >
          {items.map((it) => (
            <div key={it.key}>
              {it.divider && <div className="my-1 border-t border-gray-100" />}
              <button
                type="button"
                role="menuitem"
                disabled={it.disabled}
                onClick={() => {
                  if (it.disabled) return
                  setOpen(false)
                  it.onClick()
                }}
                className={cn(
                  'cursor-pointer w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left transition',
                  'disabled:opacity-40 disabled:cursor-not-allowed',
                  it.danger
                    ? 'text-rose-600 hover:bg-rose-50'
                    : 'text-gray-700 hover:bg-gray-50',
                )}
              >
                {it.icon && <span className={cn('shrink-0', it.danger ? 'text-rose-500' : 'text-gray-400')}>{it.icon}</span>}
                <span>{it.label}</span>
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  )
}
